import { Center, HStack, RadioGroup, Stack } from "@chakra-ui/react"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import DatePicker from "@pathwright/ui/src/components/form/form-date-picker/DatePicker"
import TextInput from "@pathwright/ui/src/components/form/form-text-input/TextInput"
import ToggleInput from "@pathwright/ui/src/components/form/form-toggle/ToggleInput"
import FieldWrapper from "@pathwright/ui/src/components/form/form-utils/FieldWrapper"
import {
  getFormError,
  validate as validateFn
} from "@pathwright/ui/src/components/form/utils"
import { useGroupQuery } from "@pathwright/graphql-client/src/generated"
import useCohortPermissions from "@pathwright/features/src/modules/cohort/useCohortPermissions"
import { useRefetchCoverPageOffers } from "@pathwright/features/src/modules/cover-pages/utils"
import { Formik } from "formik"
import { validate } from "lib/core/components/forms"
import moment from "moment"
import styled from "styled-components"
import { findErrorMessage } from "../../../../lib/core/components/forms/form-utils/validate"
import { cohortEndsWhenLicenseExpires } from "../GroupUtils"

const StyledForm = styled.form`
  padding: 25px;
  padding-top: 0;

  .DatePicker__input input {
    padding: 5px 30px !important;
  }
`

const StyledFieldWrapper = styled(FieldWrapper)`
  .FieldWrapper__label {
    padding: 0;
  }
`

const uniformLegacyStyles = {
  fontWeight: "400",
  fontSize: "1em"
}

const labelWidth = 200

const formatBound = (date) => moment(date).format("MMM D, YYYY h:mm A")

// A collection child cohort's dates must fall within the parent cohort's window.
// A null parent bound leaves that side open.
const withinParentWindow = (start, end) => (values, value) => {
  if (start && moment(value).isBefore(start))
    return `Must be on or after the collection's date (${formatBound(start)}).`
  if (end && moment(value).isAfter(end))
    return `Must be on or before the collection's date (${formatBound(end)}).`
}

const isDayWithinParentWindow = (start, end) => (day) =>
  (!start || day.isSameOrAfter(start, "day")) &&
  (!end || day.isSameOrBefore(end, "day"))

const GroupAccessForm = function ({ group, initialValues, onSubmit }) {
  const refetchCoverPageOffers = useRefetchCoverPageOffers()

  const parentId = group?.parent_offering_id
  const parentQuery = useGroupQuery({
    variables: { id: parentId },
    skip: !parentId
  })
  const parent = parentQuery.data?.group
  const parentAccessStarts = parent?.access_starts
  const parentAccessEnds = parent?.access_ends
  const parentRegistrationStarts = parent?.registration_starts
  const parentRegistrationEnds = parent?.registration_ends

  async function handleSubmit(form, resetForm) {
    await onSubmit(form, resetForm)
    await refetchCoverPageOffers()
  }

  const { hasEditorLevelAccess } = useCohortPermissions({
    cohortId: group ? group.id : null
  })

  // lock expiration if group expires same time that resource license expires (if licensed)
  let lockExpiration = false

  // if user can edit resource then they have permission to update group access ends date
  if (!hasEditorLevelAccess) {
    lockExpiration = cohortEndsWhenLicenseExpires(group)
  }

  if (!group) return null
  // Formik validation depends on the parent window, so wait for it before mounting the form.
  if (parentId && parentQuery.loading) return null

  const registrationWindow = withinParentWindow(
    parentRegistrationStarts,
    parentRegistrationEnds
  )
  const accessWindow = withinParentWindow(parentAccessStarts, parentAccessEnds)
  const isRegistrationDayValid = isDayWithinParentWindow(
    parentRegistrationStarts,
    parentRegistrationEnds
  )
  const isAccessDayValid = isDayWithinParentWindow(
    parentAccessStarts,
    parentAccessEnds
  )

  return (
    <Formik
      initialValues={initialValues}
      validate={validateFn((key, value, values) => {
        const validatorMap = {
          seats:
            values.seats_limit == 1
              ? [validate.isGreaterThanOrEqualTo(1)]
              : null,
          enrollment_starts:
            values.enrollment_window && values.enrollment_starts
              ? [
                  validate.isValidDate,
                  validate.dateIsBefore(values.enrollment_ends),
                  registrationWindow
                ]
              : null,
          enrollment_ends:
            values.enrollment_window && values.enrollment_ends
              ? [
                  validate.isValidDate,
                  validate.dateIsAfter(values.enrollment_starts),
                  registrationWindow
                ]
              : null,
          access_starts:
            values.access_window && values.access_starts
              ? [
                  validate.isValidDate,
                  validate.dateIsBefore(values.access_ends),
                  accessWindow
                ]
              : null,
          access_ends:
            values.access_window && values.access_ends
              ? [
                  validate.isValidDate,
                  validate.dateIsAfter(values.access_starts),
                  accessWindow
                ]
              : null
        }
        const validators = validatorMap[key]
        return validators
          ? findErrorMessage(...validators)(values, value)
          : null
      })}
    >
      {(form) => {
        const handleSeatsLimit = function (e) {
          const value = e.value
          // NOTE: first setting "seats" value as "seats" validation
          // depends on "seats_limit".
          if (value == 0) form.setFieldValue("seats", null)
          form.setFieldValue("seats_limit", value)
        }

        return (
          <StyledForm onSubmit={(e) => e.preventDefault()}>
            <Stack>
              <StyledFieldWrapper label="Seat limit" labelWidth={labelWidth}>
                <RadioGroup.Root
                  value={form.values.seats_limit}
                  onValueChange={handleSeatsLimit}
                  colorPalette="blue"
                >
                  <RadioGroup.Item value={0} {...uniformLegacyStyles}>
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText>Unlimited.</RadioGroup.ItemText>
                  </RadioGroup.Item>

                  <HStack gap={1}>
                    <RadioGroup.Item value={1} {...uniformLegacyStyles}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        Close registration after
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                    <TextInput
                      name="seats"
                      type="number"
                      required={form.values.seats_limit == 1}
                      minWidth={30}
                      autoSize
                      inline
                      value={form.values.seats}
                      onChange={(value, e) => form.handleChange(e)}
                      onBlur={(value, e) => form.handleBlur(e)}
                      errors={getFormError(form, "seats")}
                      onFocus={() => {
                        form.setFieldValue("seats_limit", 1)
                      }}
                    />
                    <span> join</span>
                  </HStack>
                </RadioGroup.Root>
              </StyledFieldWrapper>

              <ToggleInput
                name="enrollment_window"
                label="Limit registration window"
                labelWidth={labelWidth}
                value={form.values.enrollment_window}
                onChange={(value, e) => form.handleChange(e)}
              />
              {!!form.values.enrollment_window && (
                <DatePicker
                  name="enrollment_starts"
                  label="Registration opens"
                  placeholder="Anytime"
                  labelWidth={labelWidth}
                  optional
                  value={form.values.enrollment_starts}
                  isValidDate={isRegistrationDayValid}
                  onChange={(value) =>
                    form.setFieldValue("enrollment_starts", value)
                  }
                  errors={getFormError(
                    form,
                    "enrollment_starts",
                    false /* mustBeTouched */
                  )}
                />
              )}
              {!!form.values.enrollment_window && (
                <DatePicker
                  name="enrollment_ends"
                  label="Registration closes"
                  placeholder="Never closes"
                  labelWidth={labelWidth}
                  optional
                  value={form.values.enrollment_ends}
                  isValidDate={isRegistrationDayValid}
                  onChange={(value) =>
                    form.setFieldValue("enrollment_ends", value)
                  }
                  errors={getFormError(
                    form,
                    "enrollment_ends",
                    false /* mustBeTouched */
                  )}
                />
              )}
              <ToggleInput
                name="access_window"
                label="Limit learner access"
                labelWidth={labelWidth}
                value={form.values.access_window}
                onChange={(value, e) => form.handleChange(e)}
              />
              {!!form.values.access_window && (
                <DatePicker
                  name="access_starts"
                  label="Access opens"
                  placeholder="Anytime"
                  labelWidth={labelWidth}
                  optional
                  value={form.values.access_starts}
                  isValidDate={isAccessDayValid}
                  onChange={(value) =>
                    form.setFieldValue("access_starts", value)
                  }
                  errors={getFormError(
                    form,
                    "access_starts",
                    false /* mustBeTouched */
                  )}
                />
              )}
              {!!form.values.access_window && (
                <DatePicker
                  name="access_ends"
                  disabled={lockExpiration}
                  label="Access closes"
                  placeholder="Never closes"
                  labelWidth={labelWidth}
                  optional
                  value={form.values.access_ends}
                  isValidDate={isAccessDayValid}
                  onChange={(value) => form.setFieldValue("access_ends", value)}
                  errors={getFormError(
                    form,
                    "access_ends",
                    false /* mustBeTouched */
                  )}
                />
              )}
              <Center>
                <SubmitButton
                  disabled={!form.isValid}
                  styleType="primary"
                  type="submit"
                  onClick={() => handleSubmit(form.values)}
                >
                  Save
                </SubmitButton>
              </Center>
            </Stack>
          </StyledForm>
        )
      }}
    </Formik>
  )
}

GroupAccessForm.displayName = "GroupAccessForm"

export default GroupAccessForm
